import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Platform,
  ActivityIndicator,
  Animated,
} from 'react-native';

import { colors, fontSize, fontWeight, spacing } from '../theme';
import { AlertTriangle, X } from './icons';
import { KeyboardInsetScroll } from './shared/keyboardInset';

interface ConfirmationDialogProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonStyle?: 'primary' | 'danger';
  /**
   * Text shown next to the spinner while `onConfirm` is in flight (e.g. "Restoring…").
   * Makes a heavy async confirm obviously "working" instead of a bare, easy-to-miss spinner.
   */
  loadingText?: string;
  children?: React.ReactNode;
  /** When false, header omits the warning triangle (e.g. approver submit confirm). */
  showWarningIcon?: boolean;
  /** When true, only the primary button is shown (informational alerts). */
  hideCancelButton?: boolean;
  /**
   * `clear` — outlined neutral (Figma compact Neutral / modal Cancel).
   * `default` — legacy grey fill.
   */
  cancelButtonStyle?: 'default' | 'clear';
  /**
   * Render as an in-place faded overlay instead of a native Modal. Use when the
   * dialog must appear over content that is already inside a Modal (e.g. the
   * native rack editor pageSheet), where a second Modal would present behind it
   * or make the parent sheet recede/slide instead of fading (#995 bug 13).
   */
  inline?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  visible,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmButtonStyle = 'primary',
  loadingText,
  children,
  inline = false,
  showWarningIcon = true,
  hideCancelButton = false,
  cancelButtonStyle = 'default',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const fadeAnim = useRef(new Animated.Value(inline ? 0 : 1)).current;

  // Reset loading state when dialog is hidden (Modal doesn't unmount children)
  useEffect(() => {
    if (!visible) setIsLoading(false);
  }, [visible]);

  // Inline overlay fades itself in (no Modal animationType to lean on).
  useEffect(() => {
    if (inline && visible) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 150,
        useNativeDriver: true,
      }).start();
    }
  }, [inline, visible, fadeAnim]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } catch {
      // Keep dialog open on error so user can retry
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    onClose();
  };

  const dialogCard = (
    <View testID="confirmation-dialog" style={styles.modal}>
      <View style={styles.header}>
        {showWarningIcon ? (
          <View style={styles.iconContainer}>
            <AlertTriangle size={24} color="#f59e0b" />
          </View>
        ) : null}
        <Text style={styles.title}>{title}</Text>
        <TouchableOpacity
          style={styles.closeButton}
          onPress={onClose}
          accessibilityLabel="Close dialog"
          disabled={isLoading}
        >
          <X size={20} color={isLoading ? '#ccc' : '#666'} />
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        <Text style={styles.message}>{message}</Text>
        {children}
      </View>

      <View style={styles.footer}>
        {!hideCancelButton ? (
          <TouchableOpacity
            testID="confirmation-cancel-btn"
            style={[
              styles.cancelButton,
              cancelButtonStyle === 'clear' && styles.cancelButtonClear,
              isLoading && styles.disabledButton,
            ]}
            onPress={onClose}
            disabled={isLoading}
          >
            <Text
              style={[
                styles.cancelButtonText,
                cancelButtonStyle === 'clear' && styles.cancelButtonTextClear,
                isLoading && styles.disabledButtonText,
              ]}
            >
              {cancelText}
            </Text>
          </TouchableOpacity>
        ) : null}
        <TouchableOpacity
          testID="confirmation-confirm-btn"
          style={[
            styles.confirmButton,
            (hideCancelButton || cancelButtonStyle === 'clear') &&
              styles.confirmButtonCompact,
            confirmButtonStyle === 'danger' && styles.dangerButton,
            isLoading && styles.disabledButton,
          ]}
          onPress={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color="#fff" />
              {loadingText ? (
                <Text
                  style={[
                    styles.confirmButtonText,
                    confirmButtonStyle === 'danger' && styles.dangerButtonText,
                  ]}
                >
                  {loadingText}
                </Text>
              ) : null}
            </View>
          ) : (
            <Text
              style={[
                styles.confirmButtonText,
                confirmButtonStyle === 'danger' && styles.dangerButtonText,
              ]}
            >
              {confirmText}
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );

  const dialogBody = (
    <KeyboardInsetScroll disabled={inline}>{dialogCard}</KeyboardInsetScroll>
  );

  if (inline) {
    if (!visible) {
      return null;
    }
    return (
      <Animated.View
        style={[styles.inlineOverlay, { opacity: fadeAnim }]}
        pointerEvents="auto"
      >
        {dialogBody}
      </Animated.View>
    );
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={isLoading ? undefined : onClose}
      statusBarTranslucent
    >
      {dialogBody}
    </Modal>
  );
};

const styles = StyleSheet.create({
  inlineOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1000,
    elevation: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 8,
    width: '100%',
    maxWidth: 450,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  iconContainer: {
    marginRight: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  message: {
    fontSize: 16,
    color: '#666',
    lineHeight: 22,
    textAlign: 'left',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#eee',
    gap: 12,
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  cancelButtonClear: {
    backgroundColor: colors.backgroundSecondary,
    borderColor: colors.borderMedium,
    paddingVertical: spacing.formCompactActionPaddingVertical,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    borderRadius: spacing.cardBorderRadius,
    ...Platform.select({
      web: {
        borderStyle: 'solid' as const,
        borderWidth: 1,
        outlineWidth: 0,
        outlineStyle: 'none' as const,
        boxShadow: 'none' as const,
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
        cursor: 'pointer' as const,
      },
      default: {},
    }),
  },
  cancelButtonTextClear: {
    color: colors.textPrimary,
    fontSize: fontSize.body,
    fontWeight: fontWeight.semibold,
  },
  confirmButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 4,
    backgroundColor: colors.primary,
  },
  confirmButtonCompact: {
    paddingVertical: spacing.formCompactActionPaddingVertical,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    borderRadius: spacing.cardBorderRadius,
    ...Platform.select({
      web: {
        borderStyle: 'solid' as const,
        borderWidth: 0,
        outlineWidth: 0,
        outlineStyle: 'none' as const,
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
        cursor: 'pointer' as const,
      },
      default: {},
    }),
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  confirmButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
  },
  dangerButton: {
    backgroundColor: '#dc3545',
  },
  dangerButtonText: {
    color: '#fff',
  },
  disabledButton: {
    opacity: 0.6,
  },
  disabledButtonText: {
    color: '#999',
  },
});
